import { useEffect, useState } from "react";
import { listMovements, listWallets, type WalletMovementFull, type Wallet } from "@/lib/api";
import { formatAmount, formatDate, formatRelative } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { DataTable, Badge, type ColumnDef } from "@/components/DataTable";
import { Smartphone, CreditCard } from "lucide-react";

export function StatementsPage() {
  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [walletId, setWalletId] = useState<string>("");
  const [from, setFrom] = useState(defaultFrom());
  const [to, setTo] = useState(defaultTo());
  const [movements, setMovements] = useState<WalletMovementFull[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    listWallets().then((r) => {
      setWallets(r.data);
      if (r.data.length > 0 && !walletId) setWalletId(r.data[0].id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    if (!walletId) return;
    setLoading(true);
    try {
      const r = await listMovements({ wallet_id: walletId, date_from: from, date_to: to, limit: 200 });
      setMovements(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (walletId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletId, from, to]);

  const selected = wallets.find((w) => w.id === walletId);
  const credits = movements.filter((m) => m.amount_minor > 0).reduce((a, m) => a + m.amount_minor, 0);
  const debits = movements.filter((m) => m.amount_minor < 0).reduce((a, m) => a + Math.abs(m.amount_minor), 0);
  const net = credits - debits;

  const columns: ColumnDef<WalletMovementFull>[] = [
    {
      key: "date",
      header: "Date",
      minWidth: 130,
      cell: (m) => <span className="text-xs text-[#3f4254]" title={formatDate(m.created_at)}>{formatRelative(m.created_at)}</span>,
    },
    {
      key: "type",
      header: "Type",
      minWidth: 170,
      cell: (m) => <Badge color={movementColor(m.type)}>{labelType(m.type)}</Badge>,
    },
    {
      key: "description",
      header: "Description",
      minWidth: 240,
      wrap: true,
      cell: (m) => (
        <div>
          <div className="text-sm text-[#181c32]">{m.description || labelType(m.type)}</div>
          <div className="text-xs text-[#7e8299] font-mono">{m.reference_type} · {m.reference_id.slice(0, 18)}…</div>
        </div>
      ),
    },
    {
      key: "credit",
      header: "Crédit",
      align: "right",
      minWidth: 110,
      cell: (m) =>
        m.amount_minor > 0 ? (
          <span className="font-bold text-success-700">+{formatAmount(m.amount_minor, m.currency).replace(` ${m.currency}`, "")}</span>
        ) : (
          <span className="text-[#cbd5e0]">—</span>
        ),
    },
    {
      key: "debit",
      header: "Débit",
      align: "right",
      minWidth: 110,
      cell: (m) =>
        m.amount_minor < 0 ? (
          <span className="font-bold text-danger-700">−{formatAmount(Math.abs(m.amount_minor), m.currency).replace(` ${m.currency}`, "")}</span>
        ) : (
          <span className="text-[#cbd5e0]">—</span>
        ),
    },
    {
      key: "balance",
      header: "Solde après",
      align: "right",
      minWidth: 130,
      cell: (m) => <span className="font-semibold text-[#181c32]">{formatAmount(m.balance_after_minor, m.currency)}</span>,
    },
  ];

  return (
    <div>
      <PageHeader title="Statements" breadcrumbs={[{ label: "Finance" }, { label: "Statements" }]} />

      <div className="card mb-5">
        <div className="card-body">
          <div className="flex flex-col md:flex-row md:items-end gap-4">
            <div className="flex-1 min-w-0">
              <label className="form-label">Wallet</label>
              <select value={walletId} onChange={(e) => setWalletId(e.target.value)} className="form-input">
                {wallets.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.type === "mobile_money" ? "Mobile Money" : "Cartes"} · {w.currency} · Solde {formatAmount(w.balance_minor, w.currency)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Du</label>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="form-input" />
            </div>
            <div>
              <label className="form-label">Au</label>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="form-input" />
            </div>
            <div className="flex gap-2">
              <button onClick={() => quickRange(setFrom, setTo, 30)} className="btn btn-light btn-sm">30j</button>
              <button onClick={() => quickRange(setFrom, setTo, 90)} className="btn btn-light btn-sm">90j</button>
              <button onClick={() => quickRange(setFrom, setTo, 365)} className="btn btn-light btn-sm">1 an</button>
            </div>
          </div>
        </div>
      </div>

      {selected && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <KPI label="Wallet" value={`${selected.type === "mobile_money" ? "Mobile Money" : "Cartes"} ${selected.currency}`} icon={selected.type === "mobile_money" ? <Smartphone size={16} /> : <CreditCard size={16} />} />
          <KPI label="Total crédits" value={formatAmount(credits, selected.currency)} color="success" />
          <KPI label="Total débits" value={formatAmount(debits, selected.currency)} color="danger" />
          <KPI label="Net" value={`${net >= 0 ? "+" : ""}${formatAmount(net, selected.currency)}`} color={net >= 0 ? "success" : "danger"} />
        </div>
      )}

      <DataTable<WalletMovementFull>
        title="Mouvements"
        subtitle={`${movements.length} mouvement${movements.length > 1 ? "s" : ""} sur la période`}
        columns={columns}
        data={movements}
        rowKey={(m) => m.id}
        loading={loading}
        emptyMessage="Aucun mouvement sur cette période"
      />
    </div>
  );
}

function defaultFrom(): string {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
}
function defaultTo(): string {
  return new Date().toISOString().slice(0, 10);
}
function quickRange(setFrom: (s: string) => void, setTo: (s: string) => void, days: number) {
  const d = new Date();
  setTo(d.toISOString().slice(0, 10));
  d.setDate(d.getDate() - days);
  setFrom(d.toISOString().slice(0, 10));
}

function labelType(t: string): string {
  return (
    {
      credit_payment: "Crédit paiement",
      debit_refund: "Débit remboursement",
      debit_settlement: "Débit settlement",
      credit_settlement_failed: "Annulation settlement",
      debit_commission: "Commission",
      manual_adjustment: "Ajustement manuel",
    } as Record<string, string>
  )[t] ?? t;
}

function movementColor(t: string): "success" | "danger" | "warning" | "info" | "light" {
  if (t.startsWith("credit_")) return "success";
  if (t === "debit_refund") return "danger";
  if (t === "debit_commission") return "warning";
  if (t === "debit_settlement") return "info";
  return "light";
}

function KPI({
  label,
  value,
  icon,
  color = "primary",
}: {
  label: string;
  value: string;
  icon?: React.ReactNode;
  color?: "primary" | "success" | "danger";
}) {
  const cls = {
    primary: "text-[#181c32]",
    success: "text-success-700",
    danger: "text-danger-700",
  }[color];
  return (
    <div className="card card-body py-4">
      <div className="text-[10px] uppercase tracking-wider text-[#7e8299] font-bold flex items-center gap-1.5">
        {icon}
        {label}
      </div>
      <div className={`text-lg font-bold mt-1 ${cls}`}>{value}</div>
    </div>
  );
}
